import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion";
import { FaQuoteLeft, FaChevronLeft, FaChevronRight, FaStar, FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { useSelector, useDispatch } from "react-redux";
import { getTestimonials } from "../../slice/testimonialSlice";
import { getMediaUrl } from "../../common/mediaUrl";

const Counter = ({ value, suffix = "" }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest).toLocaleString() + suffix);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (inView) {
      const controls = animate(count, value, { duration: 2, ease: "easeOut" });
      return controls.stop;
    }
  }, [inView, value, count]);

  return <motion.span ref={ref}>{rounded}</motion.span>;
};

// Sub-component for individual testimonial cards
const TestimonialCard = ({ item, getImageUrl }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const description = item.description || "";
  
  // Logic to check if text is long enough to need "See More"
  // Roughly 100-120 characters fits in 3 lines on mobile/desktop in this layout
  const isLong = description.length > 120;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="min-w-[260px] sm:min-w-[280px] lg:min-w-[285px] xl:min-w-[285px] snap-start h-full"
    >
      <div className="bg-white rounded-3xl p-6 lg:p-8 h-full shadow-[0_10px_40px_rgba(0,0,0,0.03)] border border-orange-main/5 flex flex-col justify-between hover:shadow-2xl hover:shadow-orange-main/5 transition-all duration-500 relative">

        <div>
          <div className="flex justify-between items-start mb-5">
            <FaQuoteLeft className="text-3xl text-orange-main/10" />
            <div className="flex gap-0.5">
              {[...Array(item.rating || 5)].map((_, i) => (
                <FaStar key={i} className="text-yellow-400 text-xs" />
              ))}
            </div>
          </div>
          
          <div className="relative">
            <p className={`text-gray-500 text-sm leading-relaxed font-medium mb-4 italic transition-all duration-300 ${!isExpanded && isLong ? 'line-clamp-3' : ''}`}>
              "{t(description)}"
            </p>
            
            {isLong && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-orange-main text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 hover:gap-2 transition-all mb-4"
              >
                {isExpanded ? (
                  <>{t('testimonials_see_less')} <FaChevronUp className="text-[8px]" /></>
                ) : (
                  <>{t('testimonials_see_more')} <FaChevronDown className="text-[8px]" /></>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mt-auto pt-5 border-t border-gray-50">
          <img
            src={getImageUrl(item.clientPhoto)}
            alt={item.clientName}
            className="w-10 h-10 rounded-full object-cover border-2 border-orange-main/20 shadow-sm"
          />
          <div>
            <h4 className="font-bold text-gray-dark text-sm">{t(item.clientName)}</h4>
            <p className="text-[9px] uppercase tracking-widest text-gray-400 font-bold mt-0.5">{t(item.testimonialDate)}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ClientTestimonials = () => {
  const { t, i18n } = useTranslation();
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const { data: testimonials, loading } = useSelector((state) => state.testimonial);

  useEffect(() => {
    dispatch(getTestimonials());
  }, [dispatch, i18n.language]);

  const scroll = (direction) => {
    const { current } = scrollRef;
    if (current) {
      const scrollAmount = direction === "left" ? -400 : 400;
      current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <section className="bg-[#fff9f5] py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">

        {/* HEADER SECTION */}
        <div className="text-center">
          <motion.a
            href="https://www.google.com/search?q=khajurahorutes&rlz=1C1GCEA_enIN1161IN1162&oq=khajurahorutes&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIPCAEQLhgNGK8BGMcBGIAEMggIAhAAGA0YHjIICAMQABgNGB4yCAgEEAAYDRgeMggIBRAAGA0YHjIICAYQABgNGB4yBggHEEUYPNIBCDQ0NjlqMGo3qAIAsAIA&sourceid=chrome&source=chrome.ob&ie=UTF-8#lrd=0x3982e5fef9c9c9c5:0x8824ece00244e645,1,,,,"
            target="_blank"
            rel="noopener noreferrer"
            title="Khajuraho Routes Google Reviews"
            aria-label="Khajuraho Routes Google Reviews"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group flex items-center justify-center gap-3 mb-6 px-5 py-2.5 cursor-pointer hover:opacity-80 active:scale-95 transition-all"
          >
            <FcGoogle className="text-3xl" />
            <div className="h-4 w-px bg-gray-200" />
            <span className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 group-hover:underline group-hover:text-orange-main underline-offset-4 transition-colors">{t('testimonials_label')}</span>
            <div className="flex gap-0.5 ml-2">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-yellow-400 text-[10px]" />
              ))}
            </div>
          </motion.a>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl lg:text-4xl font-bold text-gray-dark mb-12 tracking-tight"
          >
            {t('testimonials_heading')}
          </motion.h2>

          {/* STATS BAR */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-8 lg:gap-16 border-b border-orange-main/10 pb-12"
          >
            <div className="flex flex-col items-center">
              <span className="text-3xl lg:text-4xl font-black text-orange-main">
                <Counter value={10} suffix="+" />
              </span>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">{t('testimonials_years')}</span>
            </div>
            <div className="hidden lg:block h-12 w-px bg-orange-main/10" />
            <div className="flex flex-col items-center">
              <span className="text-3xl lg:text-4xl font-black text-orange-main">
                <Counter value={4000} suffix="+" />
              </span>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">{t('testimonials_tours')}</span>
            </div>
            <div className="hidden lg:block h-12 w-px bg-orange-main/10" />
            <div className="flex flex-col items-center">
              <span className="text-3xl lg:text-4xl font-black text-orange-main">
                <Counter value={100000} suffix="+" />
              </span>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">{t('testimonials_travelers')}</span>
            </div>
            <div className="hidden lg:block h-12 w-px bg-orange-main/10" />
            <div className="flex flex-col items-center">
              <span className="text-3xl lg:text-4xl font-black text-orange-main">
                <Counter value={50} suffix="+" />
              </span>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">{t('testimonials_awards')}</span>
            </div>
          </motion.div>
        </div>

        {/* TESTIMONIALS SLIDER */}
        <div className="relative group mt-10">
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory items-stretch"
          >
            {testimonials && testimonials.length > 0 ? testimonials.map((item) => (
              <TestimonialCard key={item.id} item={item} getImageUrl={getMediaUrl} />
            )) : !loading && <div className="text-center w-full py-10 text-gray-400">{t('testimonials_empty')}</div>}
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={() => scroll("left")}
              className="w-11 h-11 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-orange-main hover:text-white transition-all shadow-sm"
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-11 h-11 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-orange-main hover:text-white transition-all shadow-sm"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </section>
  );
};

export default ClientTestimonials;
